<template>
	<component
		:is="currentLayoutComponent"
		v-if="isReady && !authStore.isLoggingOut"
	>
		<router-view />
	</component>
	<div v-else class="loading-container">
		<v-progress-circular
			indeterminate
			color="primary"
			size="64"
		></v-progress-circular>
	</div>
	<SessionTimeoutDialog v-if="isReady && monitorSession" />
	<UpgradeDialog />
	<!-- Pré-visualização de recibo e de relatório (UX-027/UX-028): montado uma
	     vez aqui para que os pontos que imprimem sigam só chamando o composable. -->
	<PrintPreviewDialog />
</template>

<script setup lang="ts">
import "@/styles/main.scss";
import { ref, onMounted, onUnmounted, watch, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useLocale } from "@/composables/useLocale";
import { useNotification } from "@/composables/useNotification";
import { registerNotificationHandlers } from "@/services/notificationService";
import {
	startSessionManager,
	stopSessionManager,
} from "@/services/sessionManager";
import SessionTimeoutDialog from "@/components/session/SessionTimeoutDialog.vue";
import PrintPreviewDialog from "@/components/common/PrintPreviewDialog.vue";
import UpgradeDialog from "@/components/billing/UpgradeDialog.vue";

// Layouts
import AppLayout from "@/layouts/AppLayout.vue";
import AuthLayout from "@/layouts/AuthLayout.vue";
import EmptyLayout from "@/layouts/EmptyLayout.vue";
import { useLayout } from "@/composables/useLayout";

// Composables
const authStore = useAuthStore();
const route = useRoute();
const router = useRouter();
const usesSession = computed(() => route.matched.some(
	(record) => record.meta.requiresAuth || record.meta.requiresGuest,
));
const monitorSession = computed(() => usesSession.value && authStore.isLoggedIn);
const { syncLocales } = useLocale();
const { layoutComponent } = useLayout();

// Layout components
const layoutComponents = {
	AppLayout,
	AuthLayout,
	EmptyLayout,
};

const currentLayoutComponent = computed(() => {
	// Usar o sistema de layouts dinâmico
	return (
		layoutComponents[layoutComponent.value as keyof typeof layoutComponents] ||
		layoutComponents.AppLayout
	);
});

// Refs
const isReady = ref(false);

// Compartilha a inicialização entre montagem e navegação, inclusive em andamento.
let sessionInitialization: Promise<void> | undefined;
const initializeSession = () => (sessionInitialization ??= authStore.initialize());

watch(usesSession, async (needed) => {
	if (!needed) return;
	try {
		await initializeSession();
	} catch (error) {
		console.error("Error initializing auth:", error);
	}
});

// Lifecycle
onMounted(async () => {
	try {
		// Registrar os handlers de notificação para uso global
		const { success, error, warning, info } = useNotification();
		registerNotificationHandlers({ success, error, warning, info });

		// Inicializa o estado de autenticação quando o componente é montado
		await router.isReady();
		if (usesSession.value) await initializeSession();
		// Sincronizar locales do Vuetify com i18n
		syncLocales();
	} catch (error) {
		console.error("Error initializing auth:", error);
	} finally {
		// Define o aplicativo como pronto, independentemente do resultado da inicialização
		isReady.value = true;
		// Se já há sessão ativa (token no storage), inicia o relógio de sessão.
		if (monitorSession.value) {
			startSessionManager();
		}
	}
});

// Inicia/encerra o monitoramento de sessão em login/logout.
watch(
	() => monitorSession.value,
	(loggedIn) => {
		if (loggedIn) {
			startSessionManager();
		} else {
			stopSessionManager();
		}
	}
);

onUnmounted(() => stopSessionManager());
</script>

<style lang="scss" scoped>
.loading-container {
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100vh;
	background: linear-gradient(135deg, rgb(var(--v-theme-surface)) 0%, rgba(var(--v-theme-background), 0.8) 100%);
}
</style>
